import { useState } from "react";
import dayjs from 'dayjs';
import { sub } from "date-fns";
import { ReloadOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { RadioChangeEvent } from "antd";
import { Space, Radio, Button, Spin, DatePicker } from "antd";

interface MetricsAggregationsProps {
  loading: boolean;
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  timeInterval: string;
  setTimeInterval: (interval: string) => void;
  metricsAggregator: string;
  setMetricsAggregator: (agg: string) => void;
  metricsDownsampling: string;
  setMetricsDownsampling: (ds: string) => void;
  loadMetrics: () => void;
  onMetricsAggregationChange: (e: RadioChangeEvent) => void;
  onMetricsDownsamplingChange: (e: RadioChangeEvent) => void;
}

function MetricsAggregations(props: MetricsAggregationsProps) {
  const { RangePicker } = DatePicker;

  const onTimeIntervalChange = (e: RadioChangeEvent) => {
    const newInterval = e.target.value as string;
    props.setTimeInterval(newInterval);
    const num = parseInt(newInterval);
    const unit = newInterval.slice(-1);
    let subObj: any = {};
    if (unit === 's') subObj.seconds = num;
    else if (unit === 'm') subObj.minutes = num;
    else if (unit === 'h') subObj.hours = num;
    else if (unit === 'd') subObj.days = num;
    const now = new Date();
    props.setEndDate(now);
    props.setStartDate(sub(now, subObj));
  };

  const handlePrevious = () => {
    const duration = props.endDate.getTime() - props.startDate.getTime();
    props.setStartDate(new Date(props.startDate.getTime() - duration));
    props.setEndDate(new Date(props.endDate.getTime() - duration));
  };

  const handleNext = () => {
    const duration = props.endDate.getTime() - props.startDate.getTime();
    props.setStartDate(new Date(props.startDate.getTime() + duration));
    props.setEndDate(new Date(props.endDate.getTime() + duration));
  };

  return (
    <Space direction="vertical" size="small" align="center">
      {props.loading && <Spin size="small" />}
      <Space direction="horizontal">
        <RangePicker
          size="small"
          value={[dayjs(props.startDate), dayjs(props.endDate)]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              props.setStartDate(dates[0].toDate());
              props.setEndDate(dates[1].toDate());
            }
          }}
          disabled={props.loading}
          showTime
        />
      </Space>
      <Space direction="horizontal">
        <Button size="small" icon={<LeftOutlined />} onClick={handlePrevious} disabled={props.loading} />
        <Radio.Group value={props.timeInterval} onChange={onTimeIntervalChange} size="small" disabled={props.loading}>
          <Radio.Button value='30s'>30s</Radio.Button>
          <Radio.Button value='1m'>1m</Radio.Button>
          <Radio.Button value='30m'>30m</Radio.Button>
          <Radio.Button value='1h'>1h</Radio.Button>
          <Radio.Button value='3h'>3h</Radio.Button>
          <Radio.Button value='6h'>6h</Radio.Button>
          <Radio.Button value='12h'>12h</Radio.Button>
          <Radio.Button value='24h'>24h</Radio.Button>
          <Radio.Button value='2d'>2d</Radio.Button>
          <Radio.Button value='7d'>7d</Radio.Button>
        </Radio.Group>
        <Button size="small" icon={<RightOutlined />} onClick={handleNext} disabled={props.loading} />
      </Space>
      <Space direction="horizontal">
      </Space>
      <Space direction="horizontal">
        <Radio.Group value={props.metricsAggregator} onChange={props.onMetricsAggregationChange} size="small">
          <Radio.Button value='AVG' disabled={props.loading}>
            avg
          </Radio.Button>
          <Radio.Button value='EXTREME' disabled={props.loading}>
            ext
          </Radio.Button>
          <Radio.Button value='MAX_VALUE' disabled={props.loading}>
            max
          </Radio.Button>
          <Radio.Button value='MIN_VALUE' disabled={props.loading}>
            min
          </Radio.Button>
          <Radio.Button value='LAST_VALUE' disabled={props.loading}>
            last
          </Radio.Button>
          <Radio.Button value='FIRST_VALUE' disabled={props.loading}>
            first
          </Radio.Button>
        </Radio.Group>

        <Radio.Group value={props.metricsDownsampling} onChange={props.onMetricsDownsamplingChange} size="small">
          <Radio.Button value='10s' disabled={props.loading}>
            10s
          </Radio.Button>
          <Radio.Button value='30s' disabled={props.loading}>
            30s
          </Radio.Button>
          <Radio.Button value='1m' disabled={props.loading}>
            1m
          </Radio.Button>
          <Radio.Button value='30m' disabled={props.loading}>
            30m
          </Radio.Button>
        </Radio.Group>
        <Button type="primary" size="small" icon={<ReloadOutlined />} onClick={props.loadMetrics} disabled={props.loading} />
      </Space>
    </Space>
  );
}

export default MetricsAggregations;